// frontend/src/hooks/useInventoryMutations.js
//
// Bulk write paths for the Inventory grid and the ItemLocation
// (per-row inventory metadata) bulk-save chained from ItemSetup.

import { apiJson } from '../queries/api';
import { DATA } from '../queries/dataChanged';
import { useApiMutation } from '../queries/useApiMutation';


/**
 * Bulk-update inventory rows (ItemLocation records).
 * Variables: { items } where items is `[{ id, fields }, ...]`.
 * Returns the backend's `{ summary, results }` response.
 */
export function useInventoryBulkUpdate() {
  return useApiMutation({
    mutationFn: ({ items }) => apiJson.post('/api/inventory/bulk-update/', { items }),
    changes: [DATA.inventory],
    edits: ['/api/inventory/'],
  });
}


/**
 * Kick off a safety-stock + ABC/XYZ classification job. Synchronous —
 * the response carries the full result. Writes each placement's safety
 * stock and each item's class.
 *
 * Variables: { calculateSafetyStock } — boolean flag controlling
 *   whether the job recomputes safety stock (otherwise classification
 *   only). Mirrors the page's checkbox.
 */
export function useRunSafetyStockJob() {
  return useApiMutation({
    mutationFn: ({ calculateSafetyStock }) =>
      apiJson.post('/api/stock-calculations/jobs/run/', {
        calculate_safety_stock: calculateSafetyStock,
      }),
    changes: [DATA.inventory, DATA.items],
  });
}


/**
 * Bulk-update the ItemLocation auxiliary table (safety stock, lead
 * time, MOQ, supplier assignment, etc.). Used by ItemSetup's chained
 * save flow after the Items bulk-update completes.
 *
 * ItemLocation carries only planning fields (safety stock, lead time, MOQ,
 * supplier assignment) — supplier is item-level now, so the cost-currency
 * confirmation gate lives on the Items endpoint, never here. This endpoint
 * never returns `requires_confirmation`.
 *
 * Variables:
 *   - items   — bulk batch (`[{ id, fields }, ...]`)
 */
export function useItemLocationBulkUpdate() {
  return useApiMutation({
    mutationFn: ({ items }) => apiJson.post('/api/item-locations/bulk-update/', { items }),
    changes: [DATA.inventory],
    edits: ['/api/item-locations/'],
  });
}
